import { ReviewInterface } from "@swivl-mono/types";
import { BarAndPieChartData } from "../../Charts/BarAndPieChart/BarAndPieChart";
import { StackedBarGraphData } from "../../Charts/StackedBarGraph/StackedBarGraph";
import { MenuOption } from "../BotAnalyticsDashboard";

// Review data interface for Dashboard integration
export interface ReviewData {
  byDateTime: {
    [key: string]: {
      total: number;
      positiveReviews: number;
      negativeReviews: number;
      dayOfWeek: string;
      byTimeOfDay: string;
      locationId: number;
      locationName: string;
      reviews: ReviewInterface[];
    };
  };
  updatedByDateTime: {
    [key: string]: {
      total: number;
      positiveReviews: number;
      negativeReviews: number;
      dayOfWeek: string;
      byTimeOfDay: string;
      locationId: number;
      locationName: string;
      reviews: ReviewInterface[];
    };
  };
}

/**
 * Transform review data for StackedBarGraph charts (Reviews and UpdatedReviews components)
 * @param data - Review data with byDateTime and updatedByDateTime
 * @param type - Chart view type (Day, TimeOfDay, DayOfWeek)
 * @param isUpdated - Whether to use updated review data (for UpdatedReviews component)
 * @returns StackedBarGraphData for positive/negative review visualization
 */
export function ReviewDataForType(data: ReviewData, type: MenuOption, isUpdated: boolean = false): StackedBarGraphData {
  let output: StackedBarGraphData = {
    ticks: [],
    segments: [[], []],
    colors: ["#F67902", "#FFA900"], // Positive (orange), Negative (light orange)
  };

  const dataSource = isUpdated ? data.updatedByDateTime : data.byDateTime;

  if (type === MenuOption.Day) {
    // Sort dates chronologically (oldest to newest)
    const sortedDays = Object.keys(dataSource).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });

    sortedDays.forEach((day) => {
      let dayDate = new Date(day + "T12:00:00.000-06:00");
      let stringVersion = String(dayDate.getMonth() + 1) + "/" + String(dayDate.getDate());
      output.ticks.push(stringVersion);

      output.segments[0].push({ x: stringVersion, y: dataSource[day].positiveReviews });
      output.segments[1].push({ x: stringVersion, y: dataSource[day].negativeReviews });
    });
    output.tickCount = 3;
  }

  if (type === MenuOption.TimeOfDay) {
    const timeSlots = {};

    // Initialize all half-hour time slots from 12:00 AM to 11:59 PM
    for (let hour = 0; hour < 24; hour++) {
      for (let minutes of [0, 30]) {
        const timeOfDay = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
        timeSlots[timeOfDay] = { total: 0, positiveReviews: 0, negativeReviews: 0 };
      }
    }

    for (const day in dataSource) {
      if (Object.prototype.hasOwnProperty.call(dataSource, day)) {
        const timeOfDay = dataSource[day].byTimeOfDay;
        if (timeSlots[timeOfDay]) {
          timeSlots[timeOfDay].total += dataSource[day].total;
          timeSlots[timeOfDay].positiveReviews += dataSource[day].positiveReviews;
          timeSlots[timeOfDay].negativeReviews += dataSource[day].negativeReviews;
        }
      }
    }

    const sortedTimeSlots = Object.keys(timeSlots).sort((a, b) => {
      const [aHour, aMinute] = a.split(":").map(Number);
      const [bHour, bMinute] = b.split(":").map(Number);
      return aHour * 60 + aMinute - (bHour * 60 + bMinute);
    });

    sortedTimeSlots.forEach((timeOfDay) => {
      output.ticks.push(timeOfDay);
      output.segments[0].push({ x: timeOfDay, y: timeSlots[timeOfDay].positiveReviews });
      output.segments[1].push({ x: timeOfDay, y: timeSlots[timeOfDay].negativeReviews });
    });

    output.tickCount = 7;
  }

  if (type === MenuOption.DayOfWeek) {
    const daysArray = [
      { day: "Mon", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
      { day: "Tue", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
      { day: "Wed", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
      { day: "Thu", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
      { day: "Fri", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
      { day: "Sat", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
      { day: "Sun", data: { total: 0, positiveReviews: 0, negativeReviews: 0 } },
    ];

    for (const day in dataSource) {
      if (Object.prototype.hasOwnProperty.call(dataSource, day)) {
        const dayOfWeek = dataSource[day].dayOfWeek;
        const dayData = daysArray.find((d) => d.day === dayOfWeek.substring(0, 3));
        if (dayData) {
          dayData.data.total += dataSource[day].total;
          dayData.data.positiveReviews += dataSource[day].positiveReviews;
          dayData.data.negativeReviews += dataSource[day].negativeReviews;
        }
      }
    }

    for (let i = 0; i < daysArray.length; i++) {
      const element = daysArray[i];
      output.ticks.push(element.day);
      output.segments[0].push({ x: element.day, y: element.data.positiveReviews });
      output.segments[1].push({ x: element.day, y: element.data.negativeReviews });
    }
    output.tickCount = 7;
  }

  return output;
}

/**
 * Transform review location data for BarAndPieChart (TopReviewLocations component)
 * @param data - Review data with location information
 * @param locationNames - Mapping of location IDs to display names
 * @returns BarAndPieChartData for location-based review visualization
 */
export function TopReviewLocations(data: ReviewData, locationNames: { [key: number]: string }): BarAndPieChartData[] {
  const locationData: { [key: number]: { total: number } } = {};

  for (const key in data.byDateTime) {
    if (Object.prototype.hasOwnProperty.call(data.byDateTime, key)) {
      const element = data.byDateTime[key];
      const locationId = element.locationId;

      if (!locationData[locationId]) {
        locationData[locationId] = { total: 0 };
      }
      locationData[locationId].total += element.total;
    }
  }

  const output: BarAndPieChartData[] = Object.keys(locationData).map((locationId) => ({
    title: locationNames[locationId] || locationId,
    value: locationData[locationId].total,
  }));

  return output;
}

/**
 * Transform rating data for BarAndPieChart (RatingSummary component)
 * @param reviews - Array of review objects
 * @returns BarAndPieChartData for 1-5 star rating distribution
 */
export function transformRatingDataForChart(reviews: ReviewInterface[]): BarAndPieChartData[] {
  const ratingCounts = reviews.reduce(
    (acc, review) => {
      acc[review.rating] = (acc[review.rating] || 0) + 1;
      return acc;
    },
    {} as { [key: number]: number },
  );

  const chartData = Object.keys(ratingCounts).map((rating) => ({
    title: `${rating} ⭐️`,
    value: ratingCounts[rating],
  }));
  return chartData;
}

/**
 * Calculate average rating from reviews
 * @param reviews - Array of review objects
 * @returns Average rating as string with 1 decimal place, or empty string if no reviews
 */
export function calculateAverageRating(reviews: ReviewInterface[]): string {
  if (!reviews || reviews.length === 0) {
    return "";
  }
  
  const average = reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length;
  return average.toFixed(1);
}

/**
 * Calculate total review counts for display
 * @param reviewData - Review data object
 * @param isUpdated - Whether to count updated reviews or regular reviews
 * @returns Object with total, positive, and negative counts
 */
export function calculateReviewTotals(reviewData: ReviewData, isUpdated: boolean = false) {
  const dataSource = isUpdated ? reviewData.updatedByDateTime : reviewData.byDateTime;
  
  return Object.values(dataSource).reduce(
    (acc: { total: number; positive: number; negative: number }, day: any) => ({
      total: acc.total + day.total,
      positive: acc.positive + day.positiveReviews,
      negative: acc.negative + day.negativeReviews,
    }),
    { total: 0, positive: 0, negative: 0 }
  );
}

/**
 * Location review statistics interface
 */
export interface LocationReviewStats {
  locationId: number;
  totalReviews: number;
  averageRating: number;
  positiveReviews: number;
  negativeReviews: number;
  allReviews: ReviewInterface[];
  ratingDistribution: { [rating: number]: number };
}

/**
 * Transform review data into location-centric statistics
 * @param reviewData - Review data object
 * @param locationNames - Mapping of location IDs to names
 * @returns Array of location statistics sorted by review count (descending)
 */
export function transformLocationReviewData(
  reviewData: ReviewData,
  locationNames: { [key: number]: string }
): LocationReviewStats[] {
  const locationMap: { [key: number]: LocationReviewStats } = {};

  // Process all review data
  for (const dateKey in reviewData.byDateTime) {
    if (Object.prototype.hasOwnProperty.call(reviewData.byDateTime, dateKey)) {
      const dayData = reviewData.byDateTime[dateKey];
      const locationId = dayData.locationId;

      // Initialize location stats if not exists
      if (!locationMap[locationId]) {
        locationMap[locationId] = {
          locationId,
          totalReviews: 0,
          averageRating: 0,
          positiveReviews: 0,
          negativeReviews: 0,
          allReviews: [],
          ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        };
      }

      // Aggregate location data
      locationMap[locationId].totalReviews += dayData.total;
      locationMap[locationId].positiveReviews += dayData.positiveReviews;
      locationMap[locationId].negativeReviews += dayData.negativeReviews;
      locationMap[locationId].allReviews.push(...dayData.reviews);

      // Update rating distribution
      dayData.reviews.forEach(review => {
        if (review.rating >= 1 && review.rating <= 5) {
          locationMap[locationId].ratingDistribution[review.rating] += 1;
        }
      });
    }
  }

  // Calculate average ratings and convert to array
  const locationStats = Object.values(locationMap).map(stats => {
    // Calculate average rating
    if (stats.allReviews.length > 0) {
      const totalRating = stats.allReviews.reduce((sum, review) => sum + review.rating, 0);
      stats.averageRating = totalRating / stats.allReviews.length;
    }

    // Sort reviews by date (newest first)
    stats.allReviews.sort((a, b) => {
      const dateA = new Date(a.reviewDate).getTime();
      const dateB = new Date(b.reviewDate).getTime();
      return dateB - dateA;
    });

    return stats;
  });

  // Sort locations by review count (descending)
  locationStats.sort((a, b) => b.totalReviews - a.totalReviews);

  return locationStats;
}

/**
 * Prepare reviews for AI summary generation by flattening all reviews from location stats
 * @param locationStats - Array of location review statistics
 * @param startDate - Optional start date filter
 * @param endDate - Optional end date filter
 * @returns Array of all reviews within date range, sorted by date (newest first)
 */
export function prepareReviewsForSummary(
  locationStats: LocationReviewStats[],
  startDate?: Date,
  endDate?: Date
): ReviewInterface[] {
  const allReviews: ReviewInterface[] = [];
  
  locationStats.forEach(stats => {
    stats.allReviews.forEach(review => {
      const reviewDate = new Date(review.reviewDate);
      
      // Apply date filters if provided
      if ((!startDate || reviewDate >= startDate) && 
          (!endDate || reviewDate <= endDate)) {
        allReviews.push(review);
      }
    });
  });
  
  // Sort by date (newest first) for better summary context
  return allReviews.sort((a, b) => {
    const dateA = new Date(a.reviewDate).getTime();
    const dateB = new Date(b.reviewDate).getTime();
    return dateB - dateA;
  });
}